import type { Notification } from '@/types';
import { cn } from '@/lib/utils';
import { 
  Bell, 
  CheckCircle2, 
  Info, 
  Clock,
  ArrowRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface NotificationListItemProps {
  notification: Notification;
  onClick?: (id: string) => void;
  currentUserId?: string;
}

const categoryIcons = {
  system: Info,
  project: Bell,
  course: CheckCircle2,
  organization: Info,
};

const priorityColors = {
  low: 'bg-blue-500/10 text-blue-500',
  medium: 'bg-yellow-500/10 text-yellow-500',
  high: 'bg-red-500/10 text-red-500',
};

export function NotificationListItem({ notification, onClick, currentUserId }: NotificationListItemProps) {
  const Icon = categoryIcons[notification.category] || Bell;
  const actor = notification.metadata?.actor;
  const isActorMe = currentUserId && actor?.id === currentUserId;
  
  // Clean up message text (strip extra quotes if any)
  let cleanMessage = notification.message?.replace(/^"|"$/g, '').replace(/^'|'$/g, '');

  // Extra safety: if it still looks like a JSON object, try to extract the text field
  if (cleanMessage?.trim().startsWith('{')) {
    try {
      // First try to extract 'text' directly with regex to avoid full parsing overhead/errors
      const textMatch = cleanMessage.match(/"text":\s*"([^"]*)"/) || cleanMessage.match(/'text':\s*['"]([^'"]*)['"]/);
      if (textMatch) {
        cleanMessage = textMatch[1];
      }
    } catch (e) {
      // Ignore errors and keep cleanMessage as is
    }
  }

  return (
    <div 
      onClick={() => onClick?.(notification.id)}
      className={cn(
        "group relative flex items-start gap-4 p-4 rounded-2xl transition-all duration-300 cursor-pointer w-full min-w-0 overflow-hidden",
        "border border-transparent hover:border-border/50 hover:bg-muted/30",
        notification.status === 'unread' ? "bg-primary/5" : "bg-transparent"
      )}
    >
      <div className="flex-shrink-0 relative">
        {actor?.photo_url ? (
          <Avatar className="h-12 w-12 rounded-xl transition-transform duration-300">
            <AvatarImage src={actor.photo_url} alt={actor.name} />
            <AvatarFallback className="rounded-xl bg-primary/10 text-primary">
              {actor.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300",
            priorityColors[notification.priority]
          )}>
            <Icon className="h-6 w-6" />
          </div>
        )}
        
        {/* Small category icon overlay if there's an actor photo */}
        {actor?.photo_url && (
          <div className={cn(
            "absolute -bottom-1 -right-1 w-5 h-5 rounded-lg flex items-center justify-center border-2 border-background",
            priorityColors[notification.priority]
          )}>
            <Icon className="h-3 w-3" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1 min-w-0">
          <div className="flex min-w-0 flex-col">
            <h4 className={cn(
              "text-sm font-semibold leading-snug",
              notification.status === 'unread' ? "text-foreground" : "text-muted-foreground"
            )}>
              {notification.title}
            </h4>
            {actor?.name && (
              <span className="text-[12px] text-muted-foreground font-medium truncate">
                by {actor.name}{isActorMe ? ' (You)' : ''}
              </span>
            )}
          </div>
          <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground whitespace-nowrap self-start mt-1 max-w-[38%]">
            <Clock className="h-3 w-3" />
            <span className="truncate">
              {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
            </span>
          </span>
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {cleanMessage}
        </p>
        
        {notification.link && (
          <div className="mt-3 flex items-center text-[12px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            View Details
            <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </div>

      {notification.status === 'unread' && (
        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-primary" />
      )}
    </div>
  );
}
